<script lang="ts">
  import cx from "classnames";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import type { GenericSpace } from "models/spaces";
  import type { Document } from "models/documents";
  import Separator from "$components/+blocks/Separator/Separator.svelte";
  import { Right, File, Squircle } from "$components/+blocks/OVIcon";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";

  export let items: [Document[], GenericSpace[]]; // this may be a member/space/forum in future
  export let command: ({ result }: { result: GenericSpace | Document }) => void;
  export let isCommunityEnabled = false;
  export let isDocumentsEnabled = false;
  export let isDarkMode = false;

  const getIcon = (item: Document | GenericSpace) => {
    if (isDarkMode && (item.iconDark || item.icon)) {
      return item.iconDark ? item.iconDark : (item.icon ?? undefined);
    }
    if (!isDarkMode && item.icon) {
      return item.icon;
    }
  };

  // pointer variables to track the current position during keyboard navigation
  // set the initial position to the first item of the first group
  let groupIndex: number = 0;
  let itemIndex: number = 0;

  const onKeyDown = async (event: { key: string }) => {
    if (event.key === "ArrowDown") {
      // circular array - using the modulo operator to reset index
      // used to continue looping over an array after it's reached the end
      itemIndex = (itemIndex + 1) % items[groupIndex].length;

      // we also need to correctly navigate between groups
      // if the current item is now the first in that group (index 0)
      if (itemIndex === 0) {
        // then increment the group index so it moves to first item of next group
        // using the modulo technique means it will circle back to the beginning after the last group
        groupIndex = (groupIndex + 1) % items.length;
      }
    }

    if (event.key === "ArrowUp") {
      // similar pattern to ArrowDown handler, but in opposite direction
      itemIndex = (itemIndex - 1) % items[groupIndex].length;

      // also handles up navigation between groups
      // if itemIndex is -1 that means it has moved out of the current group
      if (itemIndex === -1) {
        // and so we need to move to the next group by decrementing the group index
        // and add length to handle negatives
        groupIndex = (groupIndex - 1 + items.length) % items.length;
        // then also set the item index to be the last item in the group
        itemIndex = items[groupIndex].length - 1;
      }
    }

    // scroll the selected item into view
    document
      .querySelector(`[data-item="${groupIndex}-${itemIndex}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });

    if (event.key === "Enter") {
      selectItem(groupIndex, itemIndex);
      return true;
    }
    return false;
  };

  const selectItem = (groupIndex: number, itemIndex: number) => {
    const item = items[groupIndex][itemIndex];

    if (item) {
      command({ result: item });
    }
  };
</script>

<svelte:window on:keydown={onKeyDown} />

<div
  class="scrollbar-thin max-h-[400px] min-w-[300px] space-y-1 overflow-y-auto rounded-lg border border-neutral-300 bg-white p-1 drop-shadow-lg focus:outline-none dark:border-neutral-600 dark:bg-neutral-700"
>
  <nav tabIndex="0">
    <!-- DOCS -->
    {#if isDocumentsEnabled}
      <div class="ml-0.5 p-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">Docs</div>
      {#each items[0] as item, index (item.uuid)}
        <button
          data-item={`${0}-${index}`}
          class={cx(
            "group inline-flex w-full cursor-pointer items-start gap-1 rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-600 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-neutral-200",
            {
              "active bg-neutral-100 text-neutral-600 dark:bg-white/10 dark:text-neutral-200":
                groupIndex === 0 && index === itemIndex,
            }
          )}
          on:click={() => selectItem(0, index)}
        >
          {#if item.icon}
            <div class="mt-0.5">
              <Avatar mainImage={getIcon(item)} radius="rounded-[3px]" />
            </div>
          {:else}
            <OVIcon icon={File} class="mt-0.5 text-neutral-400" />
          {/if}
          <span class="inline-flex w-full justify-items-start gap-1 text-sm font-medium">
            <span class="ml-0.5 max-w-[20ch] truncate">{item.title}</span>
            {#if item.folder}
              <span class="inline-flex items-center gap-0.5 text-xs text-neutral-400">
                <OVIcon icon={Right} />
                <span class="max-w-[12ch] truncate">{item.folder.title}</span>
              </span>
            {/if}
          </span>
        </button>
      {:else}
        <div class="mx-0.5 p-2 text-sm font-medium dark:text-neutral-400">No documents found</div>
      {/each}
    {/if}
    {#if isDocumentsEnabled && isCommunityEnabled}
      <Separator class="my-3" />
    {/if}
    <!-- CHANNELS -->
    {#if isCommunityEnabled}
      <div class="ml-0.5 p-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">
        Channels
      </div>
      {#each items[1] as item, index (item.uuid)}
        <button
          data-item={`${1}-${index}`}
          class={cx(
            "group inline-flex w-full cursor-pointer items-start rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-600 dark:text-neutral-300 dark:hover:bg-white/10 dark:hover:text-neutral-200",
            {
              "active bg-neutral-100 text-neutral-600 dark:bg-white/10 dark:text-neutral-200":
                groupIndex === 1 && index === itemIndex,
            }
          )}
          on:click={() => selectItem(1, index)}
        >
          <span
            style:color={!item.icon ? item.color : undefined}
            class={cx("inline-flex w-full items-center text-sm font-medium", {
              "gap-1.5": item.icon,
              "gap-1": !item.icon,
            })}
          >
            {#if item.icon}
              <div class="mt-0.5">
                <Avatar mainImage={getIcon(item)} radius="rounded-[3px]" />
              </div>
            {:else}
              <OVIcon icon={Squircle} class="inline-block py-0.5 pl-0.5" />
            {/if}
            <span class="text-neutral-400">{item.title}</span>
          </span>
        </button>
      {:else}
        <div class="mx-0.5 p-2 text-sm font-medium dark:text-neutral-400">No channels found</div>
      {/each}
    {/if}
  </nav>
</div>
